import { SkyButton, SkyInput, SkyText, SkyTooltip } from "@skypointcloud/skypoint-ui";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { targetNameIssues } from "@shared/types/dataflows";
import {
  TARGET_TABLE_MAX,
  foldForCase,
  targetNamePrefix,
  targetNameProblem,
  targetTableName,
  type TableNameCase,
  type TableNaming,
  type WarehouseDestination,
} from "@shared/warehouse/targetTableName";
import { CheckIcon, DismissIcon, EditIcon } from "@/lib/icons";
import { stopRowSelection } from "@/lib/gridRow";

/** The row facts the name line reads (a subset of the grid's `EntityRow`). */
export interface TableNameRow {
  name: string;
  sourceSchema: string | null;
  isSelected: boolean;
  targetName?: string | null;
  tableNameCase?: TableNaming["tableNameCase"];
}

/**
 * Owner 2026-10-19: the rule a typed table part breaks, or null. Empty is no name of its own,
 * which is allowed. A stored name that does not start with the table's prefix (one saved before
 * only the table part could be chosen) is shown as text and never marked here.
 */
export function targetNameIssue(
  row: Pick<TableNameRow, "name" | "sourceSchema" | "targetName">,
  connectionName: string | null | undefined,
): string | null {
  if (!row.targetName || !connectionName) return null;
  if (!row.targetName.startsWith(targetNamePrefix(connectionName, row))) return null;
  const problem = targetNameProblem(row.targetName, connectionName, row);
  if (problem === null || problem === "prefix") return null;
  return targetNameIssues[problem];
}

/**
 * A name breaks only after an underscore (`BreakAtUnderscores`), never inside a word. `break-words`
 * is the last resort for one word wider than the column.
 */
/** Muted, one line, ellipsis only past the end of the row. */
const LINE = "min-w-0 truncate select-text";

/**
 * The slim line sits under the table's own name and spans the whole row, not the Table column,
 * which is far too narrow to hold a warehouse name beside the other columns. Once measured it is
 * positioned against the row (`tr`, made `relative` by the grid's class): its left edge is where
 * the Table cell's content starts (measured, so it follows a resize), its right edge the row's,
 * less `reserveRight` for a pinned Actions column. The cell keeps a spacer of the line's height so
 * the row grows to hold it. The line is in the cell's DOM, right after the name, so a reader meets
 * the row, then its name, then the pencil.
 *
 * #1610: the measuring lives in `RowSpan`, which mounts only while the line is shown, so its
 * layout effect always finds its elements (a row ticked after the grid mounted is measured too),
 * and it measures again at once when `editing` changes. Until the first measure the line renders
 * in flow under the name, never at the row's left edge.
 */
interface RowBox {
  left: number;
  width: number;
  height: number;
}

function useRowSpan(reserveRight: number, editing: boolean) {
  const spacer = useRef<HTMLDivElement>(null);
  const line = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<RowBox | null>(null);
  useLayoutEffect(() => {
    const element = spacer.current;
    const content = line.current;
    const rowElement = element?.closest("tr");
    if (!element || !content || !rowElement) return undefined;
    // The grid scrolls sideways when its columns do not fit the pane, so the row can be wider than
    // what is in view: the line fills the visible width, which is the scroll region's.
    const region = rowElement.closest<HTMLElement>('[role="region"]') ?? rowElement;
    const measure = () => {
      const left = element.getBoundingClientRect().left - rowElement.getBoundingClientRect().left;
      const visible = region.clientWidth || rowElement.clientWidth;
      setBox({
        left,
        width: Math.max(160, Math.min(visible, rowElement.clientWidth) - left - reserveRight),
        height: Math.ceil(content.getBoundingClientRect().height),
      });
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(rowElement);
    observer.observe(region);
    observer.observe(element);
    observer.observe(content);
    return () => observer.disconnect();
  }, [reserveRight, editing]);
  return [spacer, line, box] as const;
}

/** The fixed start, the derived name and the name shown (the stored one when there is one). */
function lineNames(
  row: TableNameRow,
  naming: TableNaming | undefined,
  destination: WarehouseDestination,
) {
  const prefix = naming?.connectionName ? targetNamePrefix(naming.connectionName, row) : null;
  const defaultName = naming
    ? targetTableName({
        ...naming,
        entityName: row.name,
        tableNameCase: row.tableNameCase ?? naming.tableNameCase,
      })
    : null;
  // #2459: a table's own name reads in the table's case (its pin, else the binding's), as it lands.
  const nameCase = row.tableNameCase ?? naming?.tableNameCase;
  const finalName = row.targetName
    ? foldForCase(row.targetName, destination, nameCase)
    : defaultName;
  return { prefix, defaultName, finalName, nameCase };
}

/** The pencil shows for a name that keeps the fixed start, on a table that can still change. */
function canEditName(
  row: TableNameRow,
  prefix: string | null,
  readOnly: boolean,
  landed: boolean,
): boolean {
  const ownsPrefix = prefix !== null && (!row.targetName || row.targetName.startsWith(prefix));
  return ownsPrefix && !readOnly && !landed;
}

/** What Save does with the typed part: a refusal to show, or the name to set (null for none). */
function saveOutcome(
  row: TableNameRow,
  prefix: string,
  part: string,
  connectionName: string | null | undefined,
): { issue: string } | { name: string | null } {
  const wrong = part
    ? targetNameIssue({ ...row, targetName: `${prefix}${part}` }, connectionName)
    : null;
  if (wrong) return { issue: wrong };
  return { name: part ? `${prefix}${part}` : null };
}

/** The danger line under the name, or nothing. */
function IssueLine({ issue, rowName }: { issue: string | null; rowName: string }) {
  if (!issue) return null;
  return (
    <SkyText
      size={200}
      color="danger"
      block
      className="min-w-0 whitespace-normal break-words"
      testId={`table-target-name-error-${rowName}`}
    >
      {issue}
    </SkyText>
  );
}

/** The table part's input after the fixed start, Save and Cancel, the live name and the refusal. */
function NameEditor({
  row,
  prefix,
  part,
  issue,
  shownIssue,
  defaultName,
  finalName,
  destination,
  nameCase,
  onPart,
  onSave,
  onCancel,
}: {
  row: TableNameRow;
  prefix: string;
  part: string;
  issue: string | null;
  shownIssue: string | null;
  defaultName: string | null;
  finalName: string;
  destination: WarehouseDestination;
  /** #2459: the row's case (its pin, else the binding's), which spells the prefix and preview. */
  nameCase: TableNameCase | null | undefined;
  onPart: (part: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const typedFull = part ? foldForCase(`${prefix}${part}`, destination, nameCase) : finalName;
  const placeholder =
    defaultName && defaultName.toLowerCase().startsWith(prefix)
      ? defaultName.toLowerCase().slice(prefix.length)
      : "";
  return (
    <div
      className="flex min-w-0 flex-col"
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          onSave();
        } else if (event.key === "Escape") {
          event.preventDefault();
          onCancel();
        }
      }}
    >
      <div className="flex min-w-0 items-center gap-1">
        <SkyText
          size={200}
          className="shrink-0 select-text"
          testId={`table-target-prefix-${row.name}`}
        >
          {foldForCase(prefix, destination, nameCase)}
        </SkyText>
        <div className="min-w-0 flex-1 [&>*]:w-full" style={{ maxWidth: "24rem" }}>
          <SkyInput
            size="small"
            testId={`table-target-name-${row.name}`}
            aria-label={`Table part of the warehouse table name for ${row.name}`}
            placeholder={placeholder}
            value={part}
            maxLength={Math.max(1, TARGET_TABLE_MAX - prefix.length)}
            autoFocus
            {...(issue ? { validationState: "error" as const } : {})}
            onChange={(event) => onPart(event.target.value.toLowerCase())}
          />
        </div>
        <SkyButton
          appearance="subtle"
          size="small"
          icon={CheckIcon}
          aria-label={`Save the warehouse table name for ${row.name}`}
          testId={`table-target-save-${row.name}`}
          onClick={onSave}
        />
        <SkyButton
          appearance="subtle"
          size="small"
          icon={DismissIcon}
          aria-label={`Cancel editing the warehouse table name for ${row.name}`}
          testId={`table-target-cancel-${row.name}`}
          onClick={onCancel}
        />
      </div>
      <SkyText
        size={200}
        color="muted"
        className={LINE}
        title={`Lands as ${typedFull}`}
        testId={`entity-target-name-${row.name}`}
      >
        Lands as {typedFull}
      </SkyText>
      <IssueLine issue={shownIssue} rowName={row.name} />
    </div>
  );
}

/** The name as text (fixed after the first import where it has landed), with the pencil if allowed. */
function NameReadout({
  row,
  finalName,
  landed,
  canEdit,
  shownIssue,
  onOpen,
}: {
  row: TableNameRow;
  finalName: string;
  landed: boolean;
  canEdit: boolean;
  shownIssue: string | null;
  onOpen: () => void;
}) {
  const text = (
    <SkyText
      size={200}
      color="muted"
      className={LINE}
      title={`Lands as ${finalName}`}
      testId={`entity-target-name-${row.name}`}
    >
      Lands as {finalName}
    </SkyText>
  );
  return (
    <div className="flex min-w-0 flex-col">
      <div
        className="flex min-w-0 items-center gap-1"
        role="group"
        aria-label={`Warehouse table name for ${row.name}: ${finalName}`}
      >
        {landed ? (
          <SkyTooltip content="Fixed after the first import.">
            <span tabIndex={0} className="min-w-0" data-testid={`table-target-fixed-${row.name}`}>
              {text}
            </span>
          </SkyTooltip>
        ) : (
          text
        )}
        {canEdit ? (
          <SkyButton
            appearance="subtle"
            size="small"
            icon={EditIcon}
            aria-label={`Edit the warehouse table name for ${row.name}`}
            testId={`table-target-edit-${row.name}`}
            onClick={onOpen}
          />
        ) : null}
      </div>
      <IssueLine issue={shownIssue} rowName={row.name} />
    </div>
  );
}

/**
 * The measured holder of the line: an in-flow spacer the row grows around, and the content, which
 * is laid over the row once its box is known (#1610). Mounted only while the line is shown.
 */
function RowSpan({
  reserveRight,
  editing,
  children,
}: {
  reserveRight: number;
  editing: boolean;
  children: ReactNode;
}) {
  const [spacerRef, lineRef, box] = useRowSpan(reserveRight, editing);
  return (
    <div
      ref={spacerRef}
      {...(box ? { style: { height: box.height } } : {})}
      onClick={stopRowSelection}
    >
      <div
        ref={lineRef}
        className={box ? "absolute z-0 min-w-0" : "min-w-0"}
        {...(box
          ? {
              style: {
                left: box.left,
                ...(box.width > 0 ? { width: box.width } : { right: reserveRight }),
              },
            }
          : {})}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * Owner 2026-10-19: a selected table shows the name it lands as in a slim muted line under its
 * own name, spanning the row: "Lands as TPE_DBO_SD5_VIEW_02" and, until the table lands, a small
 * pencil. The pencil opens that same line for editing: the fixed start as text, an input for the
 * table part only that fills the line, Save and Cancel (Enter and Escape too), the live full name
 * in the input's place of the line, and the refusal below. A table that has landed has no pencil
 * and says why on hover or focus. An unselected table shows no name.
 *
 * #1610: whether the editor is open is the grid's to say (`editing`, `onEditingChange`), so only
 * one is open at a time. The typed part and its refusal stay here and are dropped when `editing`
 * turns false, whoever turned it off.
 */
export function TableNameLine({
  row,
  naming,
  destination,
  readOnly,
  landed,
  serverIssue,
  reserveRight,
  editing,
  onEditingChange,
  onChange,
}: {
  row: TableNameRow;
  naming: TableNaming | undefined;
  destination: WarehouseDestination;
  readOnly: boolean;
  landed: boolean;
  /** The server's last refusal of this row's name, until it is changed. */
  serverIssue: string | undefined;
  /** Pixels at the row's right edge the line leaves free (a pinned Actions column). */
  reserveRight: number;
  /** Whether this row's editor is open. */
  editing: boolean;
  /** Asks the grid to open (true) or close (false) this row's editor. */
  onEditingChange: (open: boolean) => void;
  /** Sets the whole name (prefix and part), or null for none. */
  onChange: (targetName: string | null) => void;
}) {
  const [part, setPart] = useState("");
  const [issue, setIssue] = useState<string | null>(null);
  // Dropped when `editing` turns false, whoever turned it off (set during render, not in an effect).
  const [wasEditing, setWasEditing] = useState(editing);
  if (wasEditing !== editing) {
    setWasEditing(editing);
    if (!editing) {
      setPart("");
      setIssue(null);
    }
  }
  if (!row.isSelected) return null;

  const { prefix, defaultName, finalName, nameCase } = lineNames(row, naming, destination);
  if (!finalName) return null;

  const open = () => {
    setPart(prefix && row.targetName ? row.targetName.slice(prefix.length) : "");
    setIssue(null);
    onEditingChange(true);
  };
  const save = () => {
    if (prefix === null) return;
    const outcome = saveOutcome(row, prefix, part, naming?.connectionName);
    if ("issue" in outcome) {
      setIssue(outcome.issue);
      return;
    }
    onChange(outcome.name);
    onEditingChange(false);
  };
  const cancel = () => {
    setIssue(null);
    onEditingChange(false);
  };

  const isEditing = editing && prefix !== null;
  const shownIssue = editing ? issue : (serverIssue ?? null);

  const content = isEditing ? (
    <NameEditor
      row={row}
      prefix={prefix}
      part={part}
      issue={issue}
      shownIssue={shownIssue}
      defaultName={defaultName}
      finalName={finalName}
      destination={destination}
      nameCase={nameCase}
      onPart={(next) => {
        setPart(next);
        setIssue(null);
      }}
      onSave={save}
      onCancel={cancel}
    />
  ) : (
    <NameReadout
      row={row}
      finalName={finalName}
      landed={landed}
      canEdit={canEditName(row, prefix, readOnly, landed)}
      shownIssue={shownIssue}
      onOpen={open}
    />
  );

  return (
    <RowSpan reserveRight={reserveRight} editing={isEditing}>
      {content}
    </RowSpan>
  );
}
